// src/b2b/components/SelectRoom.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Les montants reçus ici sont déjà exprimés en euros.
 */
const normalizeMoney = (value) => {
  const amount = Number(value);

  return Number.isFinite(amount) ? amount : 0;
};

const eur = (value, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
  }).format(normalizeMoney(value));

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const chip = (room) =>
  String(room?.description || room?.desc || room?.label || room?.code || 'Chambre')
    .replace(/\s+/g, ' ')
    .trim();

const roomDescription = (room) =>
  String(room?.description || room?.desc || room?.label || room?.code || 'Chambre')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Détecte une réduction enfant.
 *
 * Cela fonctionne même si le backend ne transmet pas encore
 * hasChildDiscount, car PGI place actuellement l'information
 * dans la description de la chambre.
 */
const getChildDiscountInfo = (room) => {
  const description = roomDescription(room);
  const normalizedDescription = normalizeText(description);

  const hasChildDiscount =
    room?.hasChildDiscount === true ||
    Boolean(room?.childDiscount) ||
    normalizedDescription.includes('reduction enfant');

  const explicitAgeRange = room?.childDiscount?.ageRange;

  let ageRange = null;

  if (
    explicitAgeRange &&
    Number.isFinite(Number(explicitAgeRange.min)) &&
    Number.isFinite(Number(explicitAgeRange.max))
  ) {
    ageRange = {
      min: Number(explicitAgeRange.min),
      max: Number(explicitAgeRange.max),
    };
  } else {
    const ageMatch = description.match(/\((\d+)\s*-\s*(\d+)\)/);

    if (ageMatch) {
      ageRange = {
        min: Number(ageMatch[1]),
        max: Number(ageMatch[2]),
      };
    }
  }

  return {
    hasChildDiscount,
    ageRange,
  };
};

const getAllocationLabel = (room) => {
  const allocation = room?.allocation || room?.alloc || {};

  const adults = Number(allocation?.adults || 0);
  const children = Number(allocation?.children || 0);
  const infants = Number(allocation?.infants ?? allocation?.infant ?? 0);

  const parts = [];

  if (adults > 0) {
    parts.push(`${adults} adulte${adults > 1 ? 's' : ''}`);
  }

  if (children > 0) {
    parts.push(`${children} enfant${children > 1 ? 's' : ''}`);
  }

  if (infants > 0) {
    parts.push(`${infants} bébé${infants > 1 ? 's' : ''}`);
  }

  return parts.join(' + ');
};

/**
 * Total d'une ligne du combo.
 *
 * Exemple :
 * qty = 5
 * unit = 4 400 €
 * total = 22 000 €
 */
const comboItemTotal = (item) => {
  const qty = Math.max(1, Number(item?.qty || 1));

  const unit = normalizeMoney(item?.price?.unit ?? item?.unitTotal ?? item?.unitPrice ?? 0);

  if (unit > 0) {
    return Number((unit * qty).toFixed(2));
  }

  const explicitTotal = normalizeMoney(item?.price?.total);

  return Number(explicitTotal.toFixed(2));
};

/**
 * Total réel de toutes les chambres d'un combo.
 */
const comboTotal = (combo = []) => {
  if (!Array.isArray(combo) || combo.length === 0) {
    return 0;
  }

  const total = combo.reduce((sum, item) => sum + comboItemTotal(item), 0);

  return Number(total.toFixed(2));
};

const roomTotal = (room) => {
  const calculatedFromCombo = comboTotal(room?.combo);

  if (calculatedFromCombo > 0) {
    return calculatedFromCombo;
  }

  const fallback = Number(room?.roomsTotal ?? room?.total ?? room?.price?.total ?? 0);

  return Number.isFinite(fallback) ? fallback : 0;
};

/* ───────────────── Main ───────────────── */

export default function SelectRoom({
  rooms = [],
  adults = 2,
  children = 0,
  infants = 0,
  nights = 1,
  basePriceTotal,
  defaultIndex = null,
  selectedCode,
  onSelect,
}) {
  const [selected, setSelected] = useState(null);
  const userPickedRef = useRef(false);

  // Évite les warnings eslint si ces informations
  // sont conservées pour un usage ultérieur.
  void adults;
  void children;
  void infants;
  void nights;
  void basePriceTotal;

  // L'option incluse est placée en premier.
  const sortedRooms = useMemo(() => {
    const list = Array.isArray(rooms) ? [...rooms] : [];

    list.sort((a, b) => Number(b?.ui?.included === true) - Number(a?.ui?.included === true));

    return list;
  }, [rooms]);

  // Recherche de l'index de l'option incluse.
  const computedDefaultIndex = useMemo(() => {
    const includedIndex = sortedRooms.findIndex((room) => room?.ui?.included === true);

    if (
      typeof defaultIndex === 'number' &&
      defaultIndex >= 0 &&
      defaultIndex < sortedRooms.length
    ) {
      return defaultIndex;
    }

    return includedIndex >= 0 ? includedIndex : 0;
  }, [sortedRooms, defaultIndex]);

  /**
   * Total réel de l'option incluse.
   */
  const includedTotal = useMemo(() => {
    const includedRoom = sortedRooms.find((room) => room?.ui?.included === true);

    if (!includedRoom) return 0;

    return roomTotal(includedRoom);
  }, [sortedRooms]);

  // Construction du modèle utilisé par l'interface.
  const model = useMemo(() => {
    return sortedRooms.map((room, idx) => {
      const total = roomTotal(room);
      const included = room?.ui?.included === true;
      const currency = room?.price?.currency || 'EUR';
      const combo = Array.isArray(room?.combo) ? room.combo : [];

      /**
       * La différence peut être positive ou négative.
       *
       * Positive : supplément.
       * Négative : économie.
       */
      const delta =
        !included && total > 0 && includedTotal > 0
          ? Number((total - includedTotal).toFixed(2))
          : 0;

      const supplement = Number(Math.max(0, delta).toFixed(2));

      const saving = Number(Math.max(0, -delta).toFixed(2));

      return {
        key: `${room.code || 'room'}-${idx}`,
        idx,
        label: chip(room),
        total,
        isIncluded: included,
        currency,
        delta,
        supplement,
        saving,
        combo,
        data: room,
      };
    });
  }, [sortedRooms, includedTotal]);

  /**
   * Construit toujours le même payload.
   */
  const buildSelectionPayload = (item) => {
    const room = item.data;

    return {
      _idx: item.idx,
      code: room.code,
      description: room.description,

      price: {
        total: item.total,
        currency: item.currency,
      },

      total: item.total,
      roomsTotal: item.total,
      defaultRoomsTotal: includedTotal,

      unitPrice: room?.unitPrice ?? null,
      roomsQuantity: room?.roomsQuantity ?? 1,

      // Différence signée.
      roomDelta: item.delta,

      // Valeur positive uniquement lorsqu'il y a un supplément.
      supplement: item.supplement,

      // Valeur positive uniquement lorsque l'option est moins chère.
      saving: item.saving,

      fits: room?.fits ?? true,
      combo: item.combo,
    };
  };

  // Sélection initiale ou restauration.
  useEffect(() => {
    if (!model.length) return;

    if (selectedCode) {
      const idx = model.findIndex((item) => item.data?.code === selectedCode);

      if (idx >= 0 && selected !== idx) {
        const item = model[idx];

        setSelected(idx);
        userPickedRef.current = true;
        onSelect?.(buildSelectionPayload(item));
      }

      return;
    }

    const validDefaultIndex =
      typeof computedDefaultIndex === 'number' &&
      computedDefaultIndex >= 0 &&
      computedDefaultIndex < model.length;

    if (!userPickedRef.current && validDefaultIndex) {
      const item = model[computedDefaultIndex];

      setSelected(computedDefaultIndex);
      userPickedRef.current = true;
      onSelect?.(buildSelectionPayload(item));
    }
  }, [model, selectedCode, computedDefaultIndex, selected, onSelect, includedTotal]);

  if (!model.length) {
    return (
      <div className="text-primary small">
        Aucune disponibilité retournée pour ce produit à cette date.
      </div>
    );
  }

  return (
    <div className="vstack gap-2">
      {model.map((item) => {
        const isSelected = selected === item.idx;

        return (
          <label
            key={item.key}
            className={`d-flex flex-column border rounded px-3 py-2 ${
              isSelected ? 'bg-light border-primary' : 'border-200'
            }`}
            style={
              isSelected
                ? {
                    boxShadow: '0 0 0 2px rgba(13,110,253,.25)',
                  }
                : undefined
            }
          >
            <div className="d-flex align-items-start gap-2 w-100">
              <input
                type="radio"
                name="room"
                checked={isSelected}
                onChange={() => {
                  userPickedRef.current = true;
                  setSelected(item.idx);

                  const payload = buildSelectionPayload(item);

                  console.log('SELECT ROOM', {
                    code: payload.code,
                    combo: payload.combo,
                    defaultRoomsTotal: payload.defaultRoomsTotal,
                    selectedRoomsTotal: payload.roomsTotal,
                    roomDelta: payload.roomDelta,
                    supplement: payload.supplement,
                    saving: payload.saving,
                  });

                  onSelect?.(payload);
                }}
                className="mt-1"
              />

              <div className="flex-grow-1" style={{ marginLeft: 5 }}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="fw-semibold">{item.label}</div>

                  {item.isIncluded ? (
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      Inclus
                    </span>
                  ) : item.delta < 0 ? (
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      Économie {eur(item.saving, item.currency)}
                    </span>
                  ) : (
                    <span
                      className="px-3 py-1 rounded text-white text-nowrap"
                      style={{ background: '#0b1975' }}
                    >
                      +{eur(item.supplement, item.currency)}
                    </span>
                  )}
                </div>

                {/* Détail des chambres contenues dans le combo */}
                {item.combo.length > 0 && (
                  <div className="mt-2 pt-2 border-top">
                    {item.combo.map((room, roomIndex) => {
                      const qty = Math.max(1, Number(room?.qty || 1));

                      const description = roomDescription(room);

                      const currency = room?.price?.currency || item.currency || 'EUR';

                      const unitPrice = normalizeMoney(
                        room?.price?.unit ?? room?.unitTotal ?? room?.unitPrice ?? 0,
                      );

                      const totalPrice = comboItemTotal(room);

                      const allocationLabel = getAllocationLabel(room);

                      const childDiscount = getChildDiscountInfo(room);

                      return (
                        <div
                          key={`${room?.value || room?.code || 'room'}-${roomIndex}`}
                          className="d-flex justify-content-between align-items-start gap-3 py-2"
                        >
                          <div className="flex-grow-1">
                            <div className="small fw-semibold">
                              {qty > 1 ? `${qty} × ` : ''}
                              {description}
                            </div>

                            {allocationLabel && (
                              <div className="small text-muted mt-1">{allocationLabel}</div>
                            )}

                            {childDiscount.hasChildDiscount && (
                              <div className="mt-1">
                                <span className="badge bg-success-subtle text-success border border-success-subtle">
                                  Réduction enfant appliquée
                                  {childDiscount.ageRange
                                    ? ` de ${childDiscount.ageRange.min} à ${childDiscount.ageRange.max} ans`
                                    : ''}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="text-end text-nowrap">
                            {qty > 1 && unitPrice > 0 && (
                              <div className="small text-muted">
                                {qty} × {eur(unitPrice, currency)}
                              </div>
                            )}

                            <div className="fw-semibold">{eur(totalPrice, currency)}</div>

                            {childDiscount.hasChildDiscount && (
                              <div className="small text-success">Prix après réduction</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
}
